import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CURRENCIES as FALLBACK_CURRENCIES, isValidCurrencyCode } from '@/lib/currencies';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface RegistryCurrency {
  code: string;
  name: string;
  symbol: string | null;
  decimalPlaces: number;
  isCurated: boolean;
}
interface AdoptedCurrency {
  code: string;
  name: string;
  symbol: string | null;
  decimalPlaces: number;
  useCount: number;
  lastUsedAt: string | null;
}

/**
 * Searchable currency picker with an "Add other code" fallback so ops
 * can enter any ISO-shaped 3-letter code (SEK, NOK, PLN, whatever)
 * without waiting for a developer to update a hardcoded list.
 *
 * Standalone from SearchableSelect because currencies have their own
 * shape (code + name + symbol) and the "add custom" affordance needs a
 * dedicated inline row.
 */
export function CurrencySelect({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  className?: string;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [customError, setCustomError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const customInputRef = useRef<HTMLInputElement>(null);

  const qc = useQueryClient();

  // Dropdown data: this company's ADOPTED currencies (short + sorted
  // most-used first). Refresh on-demand + short cache since a fresh
  // adoption via the Add panel needs to be reflected immediately.
  const adopted = useQuery<AdoptedCurrency[]>({
    queryKey: ['currencies-adopted'],
    queryFn: () => api('/api/currencies/adopted'),
    staleTime: 5 * 60_000,
  });

  // Registry: full ISO 4217 list. Used ONLY to auto-fill name + symbol
  // in the "Add another currency" panel — never in the dropdown.
  const registry = useQuery<RegistryCurrency[]>({
    queryKey: ['currencies-registry'],
    queryFn: () => api('/api/currencies'),
    staleTime: 60 * 60_000,
    // Lazy — only fetched when the Add panel actually opens.
    enabled: showCustomInput,
  });

  const adoptMut = useMutation({
    mutationFn: (code: string) =>
      api('/api/currencies/adopted', { method: 'POST', body: { code } }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['currencies-adopted'] }); },
  });

  const list: AdoptedCurrency[] = useMemo(() => {
    if (adopted.data && adopted.data.length > 0) return adopted.data;
    // Fallback while the first fetch is in flight: show the same list
    // the founder wants pinned by default. Prevents an empty dropdown
    // during the first ~100ms after opening the form.
    const codes = ['INR', 'USD', 'EUR', 'GBP', 'CHF', 'JPY', 'KRW', 'CNY',
      'SGD', 'THB', 'MYR', 'IDR', 'AUD', 'NZD', 'AED', 'ZAR'];
    return codes.map((code) => {
      const info = FALLBACK_CURRENCIES.find((c) => c.code === code);
      return {
        code,
        name: info?.name ?? code,
        symbol: info?.symbol ?? null,
        decimalPlaces: 2,
        useCount: 0,
        lastUsedAt: null,
      };
    });
  }, [adopted.data]);
  const listByCode = useMemo(() => {
    const m = new Map<string, AdoptedCurrency>();
    for (const c of list) m.set(c.code, c);
    return m;
  }, [list]);
  const registryByCode = useMemo(() => {
    const m = new Map<string, RegistryCurrency>();
    for (const c of registry.data ?? []) m.set(c.code, c);
    // Fallback: static list — used when registry hasn't loaded yet.
    for (const c of FALLBACK_CURRENCIES) {
      if (!m.has(c.code)) {
        m.set(c.code, {
          code: c.code,
          name: c.name,
          symbol: c.symbol ?? null,
          decimalPlaces: 2,
          isCurated: true,
        });
      }
    }
    return m;
  }, [registry.data]);

  useEffect(() => {
    const onClickAway = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) {
        setOpen(false);
        setShowCustomInput(false);
        setCustomCode('');
        setCustomError(null);
      }
    };
    window.addEventListener('mousedown', onClickAway);
    return () => window.removeEventListener('mousedown', onClickAway);
  }, []);

  useEffect(() => {
    if (showCustomInput) customInputRef.current?.focus();
  }, [showCustomInput]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return list;
    return list.filter(
      (c) => c.code.toLowerCase().includes(term) || c.name.toLowerCase().includes(term),
    );
  }, [q, list]);

  const selectedInfo = value ? (listByCode.get(value.toUpperCase()) ?? registryByCode.get(value.toUpperCase())) : null;
  const displayLabel = selectedInfo
    ? `${selectedInfo.code} — ${selectedInfo.name}`
    : value
      ? `${value.toUpperCase()} — (custom)`
      : '';

  // Auto-fill preview: when ops types a 3-letter code in the "Add
  // another currency" panel, look it up in the full ISO registry so we
  // can pre-fill the name + symbol as visual confirmation.
  const customLookup = customCode.length === 3 ? registryByCode.get(customCode) : null;

  const submitCustom = () => {
    const code = customCode.trim().toUpperCase();
    if (!isValidCurrencyCode(code)) {
      setCustomError('Must be a 3-letter ISO code (e.g. SEK)');
      return;
    }
    // Adopt for this company so the currency appears in the dropdown
    // for every future OTP request (best-effort — the UI commit below
    // happens regardless so a slow adopt call doesn't block the form).
    adoptMut.mutate(code, {
      onError: () => {
        // Only surface an error for codes the backend actively rejects
        // (e.g. not in the ISO registry). Anything else — network blip,
        // 500 — the current form entry still works because the code is
        // just a string.
      },
    });
    onChange(code);
    setOpen(false);
    setShowCustomInput(false);
    setCustomCode('');
    setCustomError(null);
    setQ('');
  };

  return (
    <div ref={ref} className={cn('relative w-full', className)}>
      <input
        ref={inputRef}
        type="text"
        value={open ? q : displayLabel}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); setQ(''); }}
        placeholder="Currency"
        className={cn(
          'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm',
          'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        )}
      />
      {open && (
        <div
          className="absolute left-0 z-50 mt-1 min-w-full w-max max-w-[360px] overflow-hidden rounded-lg border border-border bg-white text-foreground shadow-xl ring-1 ring-black/5 dark:bg-neutral-900"
          role="listbox"
        >
          <div className="max-h-[260px] overflow-y-auto py-1">
            {filtered.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => { onChange(c.code); setOpen(false); setQ(''); inputRef.current?.blur(); }}
                className={cn(
                  'flex w-full items-center gap-3 whitespace-nowrap px-3 py-2 text-left text-sm transition-colors hover:bg-muted/70',
                  value === c.code && 'bg-primary/8',
                )}
                role="option"
                aria-selected={value === c.code}
              >
                <span className={cn(
                  'w-11 shrink-0 font-mono text-[13px] font-semibold tracking-wide',
                  value === c.code ? 'text-primary' : 'text-foreground/90',
                )}>
                  {c.code}
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground">{c.name}</span>
                {c.symbol && (
                  <span className="w-10 shrink-0 text-right font-mono text-[11px] text-muted-foreground/60">
                    {c.symbol.trim()}
                  </span>
                )}
              </button>
            ))}
            {filtered.length === 0 && !showCustomInput && (
              <div className="px-3 py-3 text-sm text-muted-foreground">
                No match for &ldquo;{q}&rdquo;. Add it below.
              </div>
            )}
          </div>
          {showCustomInput ? (
            <div className="border-t bg-muted/30 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Add currency
                </span>
                <button
                  type="button"
                  onClick={() => { setShowCustomInput(false); setCustomCode(''); setCustomError(null); }}
                  className="text-xs text-muted-foreground hover:text-foreground"
                  aria-label="Cancel"
                >
                  ✕
                </button>
              </div>
              <input
                ref={customInputRef}
                type="text"
                value={customCode}
                onChange={(e) => {
                  setCustomCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3));
                  setCustomError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); submitCustom(); }
                  if (e.key === 'Escape') { setShowCustomInput(false); setCustomCode(''); setCustomError(null); }
                }}
                placeholder="ABC"
                className="mb-2 flex h-9 w-full rounded-md border border-input bg-white px-3 font-mono text-base tracking-widest shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:bg-neutral-950"
                autoComplete="off"
                spellCheck={false}
              />
              {/* Auto-fill preview: when the typed code matches an ISO
                  registry entry, show the resolved name + symbol so ops
                  can confirm before committing. */}
              {customLookup ? (
                <div className="mb-3 flex items-center justify-between rounded-md bg-primary/8 px-3 py-2 text-xs">
                  <span className="text-foreground/90">
                    <span className="font-medium">{customLookup.name}</span>
                    {customLookup.symbol && (
                      <span className="ml-2 text-muted-foreground">({customLookup.symbol.trim()})</span>
                    )}
                  </span>
                  <span className="rounded bg-primary/15 px-1.5 py-0.5 font-medium text-primary">match</span>
                </div>
              ) : customCode.length === 3 ? (
                <div className="mb-3 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                  Not in the ISO 4217 registry — will still work, but the symbol will show as {customCode}.
                </div>
              ) : (
                <p className={cn(
                  'mb-3 text-xs',
                  customError ? 'text-destructive' : 'text-muted-foreground',
                )}>
                  {customError ?? 'ISO 4217 · 3 uppercase letters (e.g. SEK, PLN, ISK)'}
                </p>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={submitCustom}
                  disabled={customCode.length !== 3}
                  className="h-8 flex-1 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Use {customCode || 'code'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCustomInput(false); setCustomCode(''); setCustomError(null); }}
                  className="h-8 rounded-md border border-input bg-white px-3 text-sm text-muted-foreground hover:bg-muted dark:bg-neutral-950"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowCustomInput(true)}
              className="flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm font-medium text-primary hover:bg-accent"
            >
              <span className="text-base leading-none">+</span>
              <span>Add a currency to this list…</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
